// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import locationTypeTranslations from 'infras/i18n/locales/location_type.json';
import eventPriorityTranslations from 'infras/i18n/locales/event_priority.json';
import eventTagTranslations from 'infras/i18n/locales/event_tag.json';

import { SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { css, ThemeProvider } from '@emotion/react';

import { useRoutineForm } from './hooks';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  fontSize,
  mediaMaxWithLayoutTheme,
  spacing,
  borderRadius,
  fontWeight,
  forceLayoutTheme,
  isMediaMax,
} from 'presentations/views/styles';
import {
  blue03,
  gray01,
  gray02,
  gray04,
  gray05,
  gray06,
  guest,
  primary,
  white,
} from 'presentations/views/styles/color';
import { LinkType } from 'domains/models/routine/linkType';

import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import DateTimeSelect from 'presentations/views/components/molecules/DateTimeSelect';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import RoutineScheduleForm from 'presentations/views/components/organisms/RoutineScheduleForm';
import WorkflowList from 'presentations/views/components/organisms/WorkflowList';
import Image from 'presentations/views/components/atoms/Image';
import Link from 'presentations/views/components/atoms/Link';
import Select from 'presentations/views/components/atoms/Select';
import TextField from 'presentations/views/components/atoms/TextField';
import TextArea from 'presentations/views/components/atoms/TextArea';
import Button from 'presentations/views/components/atoms/Button';
import { Role as EventTypeRole } from 'domains/models/event_type_user/role';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import MenuItem from 'presentations/views/components/atoms/MenuItem';
import { LocationType } from 'domains/models/event_type/locationType';
import { Role } from 'domains/models/team_user/role';
import { DisplayType } from 'domains/models/event_type/displayType';
import { UserAppItem } from 'presentations/api/schema/account';
import { EventDetailItem } from 'presentations/api/schema/event';
import { isEmail } from 'infras/validator';
import { Transparency } from 'domains/models/event/transparency';
import { helpPath } from 'presentations/routes/path';
import EventMetaTag from 'presentations/views/components/molecules/EventMetaTag';
import {
  getPriorityOptions,
  getTagOptions,
} from 'presentations/views/helpers/event';
import { Behavior } from 'domains/models/event/behavior';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import AutoCompleteTeamUserField from '../../molecules/AutoCompleteTeamUserField';
import { UserType } from 'domains/models/event_type_user/user_type';
import SendUpdatesDialog from '../SendUpdatesDialog';
import sanitizeHtml from 'sanitize-html';
import { SourceType } from 'domains/models/event_source/sourceType';

export interface Props {
  viewer: ViewerItem;
  routineEvent: EventDetailItem | null;
  onCancel?: any;
  onCreateEvent?: any;
  onUpdateEvent?: any;
  userApp: UserAppItem | null;
  forceTabletLayout?: boolean;
  isCreate?: boolean;
  isPopover?: boolean;
  isPopoverOpen?: boolean;
  behavior?: Behavior;
  // viewType?: EventCreateViewType;
}

export default function Component(props: Props) {
  const t = translateInComponent([
    translations,
    locationTypeTranslations,
    eventPriorityTranslations,
    eventTagTranslations,
  ]);

  const isTablet = props.forceTabletLayout ?? isMediaMax('tablet');
  const isViewerTeamMember = props.viewer.role === Role.Member;

  const { state, actions, routineScheduleForms } = useRoutineForm(props);
  const eventUsers: EventTypeUserListItem[] = [];
  state.selectedTeamUsers.forEach((u) => {
    if (typeof u !== 'string' && u.userType !== UserType.NotifyHost) {
      eventUsers.push(u);
    }
  });

  eventUsers.sort((a, b) => {
    if (typeof a === 'string') return 1;
    if (typeof b === 'string') return -1;
    if (a.userType === UserType.Guest) return 1;
    if (b.userType === UserType.Guest) return -1;
    if (a.role < b.role) return -1;
    if (a.role > b.role) return 1;
    return 0;
  });
  const owners = eventUsers.filter((u) => {
    if (typeof u === 'string') {
      return false;
    } else {
      return u.role === EventTypeRole.Admin;
    }
  });
  const priorityOptions = getPriorityOptions(t);
  const tagOptions = getTagOptions(t);
  const endDateOptions = [
    { text: t('End date none'), value: 0 },
    { text: t('End date specified'), value: 1 },
  ];
  const repeatOptions = [
    { text: t('Repeat no'), value: 0 },
    { text: t('Repeat yes'), value: 1 },
  ];
  const visibilityOptions = [
    { icon: 'earth', text: t('privacy 1'), value: DisplayType.Public },
    {
      icon: 'lock',
      text: t('privacy 2'),
      value: DisplayType.TeamPublic,
    },
    { icon: 'lock', text: t('privacy 3'), value: DisplayType.Private },
  ];
  const transparencyOption = [
    { text: t('transparency 1'), value: Transparency.Opaque },
    { text: t('transparency 2'), value: Transparency.Transparent },
  ];
  const remindOptions = [
    { text: t('Enable'), value: 1 },
    { text: t('Disable'), value: 0 },
  ];
  const roleOptions = [
    { value: EventTypeRole.Admin, text: t(`role_${EventTypeRole.Admin}`) },
    { value: EventTypeRole.Editor, text: t(`role_${EventTypeRole.Editor}`) },
    { value: EventTypeRole.Viewer, text: t(`role_${EventTypeRole.Viewer}`) },
  ];
  const roleWithoutAdminOptions = [
    { value: EventTypeRole.Editor, text: t(`role_${EventTypeRole.Editor}`) },
    { value: EventTypeRole.Viewer, text: t(`role_${EventTypeRole.Viewer}`) },
  ];
  const EventTypeUseRoleOptions = [
    {
      value: EventTypeRole.Admin,
      text: t(`eventTypeUser_${EventTypeRole.Admin}`),
    },
    {
      value: EventTypeRole.Editor,
      text: t(`eventTypeUser_${EventTypeRole.Editor}`),
    },
    {
      value: EventTypeRole.Viewer,
      text: t(`eventTypeUser_${EventTypeRole.Viewer}`),
    },
  ];

  // const slackOptions = state.slackChannels.map(
  //   (channel: SlackChannelListItem) => {
  //     return { text: formatSlackChannelName(channel), value: channel.id };
  //   },
  // );
  // if (
  //   state.slackChannel &&
  //   !state.slackChannels.some(
  //     (slackChannel) => slackChannel.id === state.slackChannel,
  //   )
  // ) {
  //   slackOptions.unshift({
  //     text:
  //       slackOptions.length === 0 ? t('slack channel') : t('private channel'),
  //     value: state.slackChannel,
  //   });
  // } else {
  //   slackOptions.unshift({ text: t('unspecified'), value: '' });
  // }

  const ParticipantSection = (
    <>
      <FieldTitle>{t('participant')}</FieldTitle>
      {state.isEventTypeEvent ? (
        <>
          <AutoCompleteTeamUserField
            name={'eventUsers'}
            viewer={props.viewer}
            isExcludeViewer={false}
            selectedTeamUserIds={state.selectedTeamUserIds}
            onUpdate={actions.onAddNewUser}
            isSupportString={false}
            size="small"
            placeholder={t('Add User')}
            variant="filled"
            isDisabled={false}
          />

          {eventUsers.map((user, index) => {
            const isFirstItem = index === 0;
            const isViewer = user.uid === props.viewer.uid;
            const isLimitedRole = isViewerTeamMember && isViewer;
            const isGuest = user.userType === UserType.Guest;
            return (
              <EventTypeUserItem
                key={`${user.uid}_${user.userType}`}
                isFirstItem={isFirstItem}
              >
                <EventTypeUserContents>
                  <EventTypeUserMainItem>
                    <EventTypeUserListItemContainer>
                      <EventUserAvatar
                        src={user.profileImage}
                        alt={user.name}
                      />
                      <UserName>{user.name}</UserName>
                      {!isGuest &&
                        !isLimitedRole &&
                        (owners.length > 1 ||
                          user.role > EventTypeRole.Admin) &&
                        state.selectedTeamUsers.length > 1 && (
                          <IconButtonWrap
                            onClick={(e: SyntheticEvent) =>
                              actions.onDeleteSelectedEventUser(user)
                            }
                          >
                            <DeleteIcon name="close" />
                          </IconButtonWrap>
                        )}
                    </EventTypeUserListItemContainer>
                  </EventTypeUserMainItem>

                  <EventTypeUserActionItem>
                    <EventUserSelect
                      name="selectedUserRole"
                      size="small"
                      onChange={(e: any) =>
                        actions.onChangeSelectedEventUserRole(e, user)
                      }
                      value={user.role}
                      options={isGuest ? roleOptions : EventTypeUseRoleOptions}
                      isDisabled={isLimitedRole || isGuest}
                      variant="filled"
                    />
                  </EventTypeUserActionItem>
                </EventTypeUserContents>
              </EventTypeUserItem>
            );
          })}
        </>
      ) : (
        <>
          <AutoCompleteTeamUserField
            name={'eventUsers'}
            viewer={props.viewer}
            isExcludeViewer={false}
            selectedTeamUserIds={state.selectedTeamUserIds}
            onUpdate={actions.onAddNewUser}
            isSupportString={true}
            size="small"
            isDisabled={false}
          />

          {state.selectedTeamUsers.map((user, index) => {
            const isFirstItem = index === 0;
            if (typeof user === 'string') {
              return (
                <EventTypeUserItem key={user} isFirstItem={isFirstItem}>
                  <EventTypeUserContents>
                    <EventTypeUserMainItem>
                      <EventTypeUserListItemContainer>
                        <WarnIcon name="alert-circle" />
                        <UserName>{user}</UserName>
                        {state.selectedTeamUsers.length > 1 && (
                          <IconButtonWrap
                            onClick={(e: SyntheticEvent) =>
                              actions.onDeleteSelectedUser(index)
                            }
                          >
                            <DeleteIcon name="close" />
                          </IconButtonWrap>
                        )}
                      </EventTypeUserListItemContainer>
                    </EventTypeUserMainItem>
                  </EventTypeUserContents>
                </EventTypeUserItem>
              );
            } else {
              const isExternalGuest = isEmail(user.uid);

              const isViewer = user.uid === props.viewer.uid;
              const isLimitedRole = isViewerTeamMember && isViewer;
              const options =
                isViewerTeamMember && !isViewer
                  ? roleWithoutAdminOptions
                  : roleOptions;
              return (
                <EventTypeUserItem key={user.uid} isFirstItem={isFirstItem}>
                  <EventTypeUserContents>
                    <EventTypeUserMainItem>
                      <EventTypeUserListItemContainer>
                        <EventUserAvatar
                          src={user.profileImage}
                          alt={user.name}
                        />
                        <UserName>{user.name}</UserName>
                        {!isLimitedRole && state.selectedTeamUsers.length > 1 && (
                          <IconButtonWrap
                            onClick={(e: SyntheticEvent) =>
                              actions.onDeleteSelectedUser(index)
                            }
                          >
                            <DeleteIcon name="close" />
                          </IconButtonWrap>
                        )}
                      </EventTypeUserListItemContainer>
                    </EventTypeUserMainItem>

                    <EventTypeUserActionItem>
                      <SelectWrap
                        name="selectedUserRole"
                        size="small"
                        onChange={(e: any) =>
                          actions.onChangeSelectedUserRole(e, index)
                        }
                        value={user.role}
                        options={options}
                        isDisabled={isLimitedRole || isExternalGuest}
                        variant="filled"
                      />
                    </EventTypeUserActionItem>
                  </EventTypeUserContents>
                </EventTypeUserItem>
              );
            }
          })}
        </>
      )}
    </>
  );

  const VisibilityMetaTag = state.isEventTypeEvent ? null : (
    <MetaTagItem>
      <EventMetaTag
        iconName={state.visibility === DisplayType.Public ? 'earth' : 'lock'}
        options={visibilityOptions}
        selectedValue={state.visibility}
        tooltipText={t('privacy')}
        onChange={actions.onChangeVisibility}
      />
    </MetaTagItem>
  );

  const isUseTimePicker = state.sourceType === SourceType.EventType;

  return (
    <Container>
      <form>
        <Content>
          <MainContainer>
            <Form isCreate={props.isCreate}>
              <MetaTagList>
                {props.isCreate && VisibilityMetaTag}
                <MetaTagItem>
                  <EventMetaTag
                    iconName="flag-triangle"
                    options={priorityOptions}
                    selectedValue={state.priority}
                    tooltipText={t('priority')}
                    onChange={actions.onChangePriority}
                  />
                </MetaTagItem>
                {/* <MetaTagItem>
                <EventMetaTag
                  iconName="label"
                  options={tagOptions}
                  selectedValue={state.tag}
                  tooltipText={t('tag')}
                  onChange={actions.onChangeTag}
                />
              </MetaTagItem> */}
                {!props.isCreate && VisibilityMetaTag}
                {props.onCancel && props.isPopover && (
                  <CloseButtonWrap onClick={props.onCancel}>
                    <CloseIcon name="close" size={fontSize(20)} />
                  </CloseButtonWrap>
                )}
              </MetaTagList>
              <NameFormField>
                <TextFieldWrap
                  name="name"
                  value={state.name}
                  placeholder={t('name placeholder')}
                  onChange={actions.onChange}
                  errors={state.errors['name']}
                  isAutoFocus
                  variant="standard"
                  isFullWidth
                  isContainerFullWidth
                  inputRef={state.nameInputRef}
                  autoComplete="off"
                />
              </NameFormField>

              {(props.isCreate || props.behavior === Behavior.Work) && (
                <CreateTypeContainer>
                  <CreateType
                    isActive={!state.isTask}
                    onClick={actions.onChangeIsTask}
                  >
                    {t('Event')}
                  </CreateType>
                  <CreateType
                    isActive={state.isTask}
                    onClick={actions.onChangeIsTask}
                  >
                    {t('Task')}
                  </CreateType>
                </CreateTypeContainer>
              )}

              <Section>
                {isTablet ? (
                  <>
                    <FormField>
                      <InlineFieldTitle>{t('Allday')}</InlineFieldTitle>
                      <AlldayContainer>
                        <Checkbox
                          name="allday"
                          value={1}
                          onChange={actions.onChange}
                          isChecked={state.isAllday}
                          color="default"
                          isDisabled={
                            state.startedAt === null ||
                            state.endedAt === null ||
                            state.isEventTypeEvent
                          }
                        />
                      </AlldayContainer>
                    </FormField>
                    <FormField>
                      <InlineFieldTitle>{t('Started At')}</InlineFieldTitle>
                      {state.isAllday ? (
                        <DateTimeSelect
                          timeZone={state.timezone}
                          date={state.startedAt}
                          isAllday={state.isAllday}
                          isHideToday
                          onSelect={actions.onChangeStartedAt}
                          selectable24hour
                        />
                      ) : (
                        <DateTimeSelect
                          timeZone={state.timezone}
                          date={state.startedAt}
                          hour={state.startedAt?.hour}
                          minute={state.startedAt?.minute}
                          isAllday={state.isAllday}
                          isHideToday
                          onSelect={actions.onChangeStartedAt}
                          selectable24hour
                          timePicker={isUseTimePicker}
                        />
                      )}
                    </FormField>
                    <FormField>
                      <InlineFieldTitle>{t('Ended At')}</InlineFieldTitle>
                      {state.isAllday ? (
                        <DateTimeSelect
                          timeZone={state.timezone}
                          date={
                            state.endedAt
                              ? state.endedAt.minus({
                                  day: state.isAllday ? 1 : 0,
                                })
                              : null
                          }
                          isAllday={state.isAllday}
                          isHideToday
                          onSelect={actions.onChangeEndedAt}
                          minDate={state.startedAt}
                        />
                      ) : (
                        <DateTimeSelect
                          timeZone={state.timezone}
                          date={state.endedAt}
                          hour={state.endedAt?.hour}
                          minute={state.endedAt?.minute}
                          isAllday={state.isAllday}
                          isHideToday
                          onSelect={actions.onChangeEndedAt}
                          minDate={state.startedAt}
                          timePicker={isUseTimePicker}
                        />
                      )}
                    </FormField>
                    <FormField>
                      <InlineFieldTitle>{t('timezone')}</InlineFieldTitle>
                      <TimeZoneSelect
                        name="timezone"
                        value={state.timezone}
                        options={state.timezoneOptions}
                        onChange={actions.onChange}
                        errors={state.errors['timezone']}
                        variant="filled"
                      />
                    </FormField>
                  </>
                ) : (
                  <FormField>
                    <DateTimeSelect
                      timeZone={state.timezone}
                      date={state.startedAt}
                      hour={state.startedAt?.hour}
                      minute={state.startedAt?.minute}
                      isAllday={state.isAllday}
                      onSelect={actions.onChangeStartedAt}
                      timePicker={isUseTimePicker}
                    />
                    <DateTimeHyphen>{t('〜')}</DateTimeHyphen>

                    <DateTimeSelect
                      timeZone={state.timezone}
                      date={
                        state.endedAt
                          ? state.endedAt.minus({ day: state.isAllday ? 1 : 0 })
                          : null
                      }
                      hour={state.endedAt?.hour}
                      minute={state.endedAt?.minute}
                      isAllday={state.isAllday}
                      onSelect={actions.onChangeEndedAt}
                      timePicker={isUseTimePicker}
                    />
                    <TimeZoneSelect
                      name="timezone"
                      value={state.timezone}
                      options={state.timezoneOptions}
                      onChange={actions.onChange}
                      errors={state.errors['timezone']}
                      variant="filled"
                    />
                    <AlldayContainer>
                      <Checkbox
                        name="allday"
                        value={1}
                        onChange={actions.onChange}
                        isChecked={state.isAllday}
                        color="default"
                        isDisabled={
                          state.startedAt === null ||
                          state.endedAt === null ||
                          state.isEventTypeEvent
                        }
                        label={t('Allday')}
                      />
                    </AlldayContainer>
                  </FormField>
                )}
                {!state.isTask && (
                  <>
                    <TopAlignFormField>
                      <InlineFieldTitle>{t('Schedule')}</InlineFieldTitle>
                      <DetailContainer>
                        <RepeatSelect
                          name="isRepeat"
                          value={state.isRepeat ? '1' : '0'}
                          options={repeatOptions}
                          onChange={actions.onChange}
                          errors={state.errors['isRepeat']}
                          variant="filled"
                          isFullWidth={isTablet && !state.isEventTypeEvent}
                          isContainerFullWidth={isTablet}
                          isDisabled={state.isEventTypeEvent}
                        />
                        {state.isRepeat &&
                          state.schedules.map((schedule, index) => (
                            <RoutineScheduleFormContainer key={index}>
                              <RoutineScheduleForm
                                startedAt={state.startedAt}
                                isAllday={state.isAllday}
                                schedule={schedule}
                                index={index}
                                onSubmit={actions.onSubmitSchedule}
                                onDelete={actions.onDeleteSchedule}
                                deletable={state.schedules.length > 1}
                                {...routineScheduleForms[index].state}
                                {...routineScheduleForms[index].actions}
                              />
                            </RoutineScheduleFormContainer>
                          ))}
                      </DetailContainer>
                    </TopAlignFormField>
                    {state.isRepeat && (
                      <TopAlignFormField>
                        <InlineFieldTitle>{t('End date')}</InlineFieldTitle>
                        <DetailContainer>
                          <EndDateSelect
                            name="endDate"
                            value={state.endDate}
                            options={endDateOptions}
                            onChange={actions.onChange}
                            errors={state.errors['endDate']}
                            variant="filled"
                            isDisabled={state.isEventTypeEvent}
                          />
                          {state.endDate === 1 && (
                            <DateTimeSelect
                              timeZone={state.timezone}
                              date={state.endDateSpecified}
                              hour={state.endDateSpecified?.hour}
                              minute={state.endDateSpecified?.minute}
                              isAllday
                              onSelect={actions.onChangeEndDateSpecified}
                              isDisabled={state.isEventTypeEvent}
                            />
                          )}
                        </DetailContainer>
                      </TopAlignFormField>
                    )}
                  </>
                )}
              </Section>

              {!state.isTask && isTablet && (
                <Section>{ParticipantSection}</Section>
              )}

              {!state.isTask && (
                <TopAlignFormField>
                  <FixedWidthInlineFieldTitle>
                    {t('location')}
                  </FixedWidthInlineFieldTitle>
                  <LocationContainer>
                    <LocationSelect
                      value={state.locationType}
                      onChange={actions.onChangeLocation}
                      isNative={false}
                      isFullWidth={!state.isEventTypeEvent}
                      isContainerFullWidth
                      variant="filled"
                      isDisabled={state.isEventTypeEvent}
                    >
                      <MenuItem value="unspecified">
                        {t('location unspecified')}
                      </MenuItem>
                      {state.locationList.map((locationType) => (
                        <MenuItem value={locationType} key={locationType}>
                          <LocationImage
                            src={`locations/${locationType}.svg`}
                            alt={t(`locationType_${locationType}`)}
                          />
                          {t(`locationType_${locationType}`)}
                        </MenuItem>
                      ))}
                    </LocationSelect>
                    {state.locationType === LocationType.InPerson && (
                      <>
                        <LocationTextFieldContainer>
                          <LocationTitle>{t('locationAddress')}</LocationTitle>
                          <TextFieldWrap
                            name="locationAddress"
                            value={state.locationAddress}
                            placeholder={t('locationAddress placeholder')}
                            onChange={actions.onChange}
                            errors={state.errors['locationAddress']}
                            variant="filled"
                            isFullWidth
                          />
                        </LocationTextFieldContainer>
                        <LocationTextFieldContainer>
                          <LocationTitle>{t('locationAccess')}</LocationTitle>
                          <TextFieldWrap
                            name="locationAccess"
                            value={state.locationAccess}
                            placeholder={t('locationAccess placeholder')}
                            onChange={actions.onChange}
                            errors={state.errors['locationAccess']}
                            variant="filled"
                            isFullWidth
                          />
                        </LocationTextFieldContainer>
                      </>
                    )}
                    {state.locationType === LocationType.Phone && (
                      <LocationTextFieldContainer>
                        <TextFieldWrap
                          name="locationPhoneNumber"
                          value={state.locationPhoneNumber}
                          placeholder={t('locationPhoneNumber placeholder')}
                          onChange={actions.onChange}
                          errors={state.errors['locationPhoneNumber']}
                          variant="filled"
                          isFullWidth
                        />
                      </LocationTextFieldContainer>
                    )}
                    {state.locationType === LocationType.Custom && (
                      <LocationTextFieldContainer>
                        <TextFieldWrap
                          name="locationCustom"
                          value={state.locationCustom}
                          placeholder={t('locationCustom placeholder')}
                          onChange={actions.onChange}
                          errors={state.errors['locationCustom']}
                          variant="filled"
                          isFullWidth
                        />
                      </LocationTextFieldContainer>
                    )}
                  </LocationContainer>
                </TopAlignFormField>
              )}

              {!props.isCreate && (
                <>
                  <FormField>
                    <FixedWidthInlineFieldTitle>
                      {t('remind')}
                      {/* <HelpLink href={helpPath('5138334')} target="_blank">
                      <HelpIcon name="help" color="inherit" />
                    </HelpLink> */}
                    </FixedWidthInlineFieldTitle>
                    <FixedWidthSelect
                      name="isRemind"
                      value={state.isRemind ? 1 : 0}
                      options={remindOptions}
                      onChange={actions.onChange}
                      errors={state.errors['isRemind']}
                      variant="filled"
                      isDisabled={state.isEventTypeEvent}
                    />
                  </FormField>

                  {/* <FormField>
                  <FixedWidthInlineFieldTitle>
                    {t('slackChannel')}
                    <InfoIconContainer>
                      <Tooltip
                        title={t('slackChannel description')}
                        placement="top"
                      >
                        <InfoIcon name="information" color="inherit" />
                      </Tooltip>
                    </InfoIconContainer>
                  </FixedWidthInlineFieldTitle>
                  <FixedWidthSelect
                    name="slackChannel"
                    options={slackOptions}
                    value={state.slackChannel}
                    onChange={actions.onChange}
                    isDisabled={slackOptions.length <= 1}
                    errors={state.errors['slackChannel']}
                    variant="filled"
                  />
                </FormField> */}

                  {!state.isTask && (
                    <FormField>
                      <FixedWidthInlineFieldTitle>
                        {t('transparency')}
                        <HelpLink href={helpPath('7155248')} target="_blank">
                          <HelpIcon name="help" color="inherit" />
                        </HelpLink>
                      </FixedWidthInlineFieldTitle>
                      <FixedWidthSelect
                        name="transparency"
                        value={state.transparency}
                        options={transparencyOption}
                        onChange={actions.onChange}
                        errors={state.errors['transparency']}
                        variant="filled"
                        isDisabled={state.isEventTypeEvent}
                      />
                    </FormField>
                  )}
                  <div>
                    <FieldTitle>{t('description')}</FieldTitle>
                    <DescriptionContainer>
                      <DescriptionTextArea
                        name={'description'}
                        onChange={actions.onChange}
                        value={sanitizeHtml(state.description ?? '', {
                          allowedTags: [],
                        })}
                        variant="filled"
                        isFullWidth={true}
                        placeholder={t('description placeholder')}
                        rows={4}
                        errors={state.errors['description']}
                        maxRows={15}
                      />
                    </DescriptionContainer>
                  </div>
                </>
              )}
            </Form>
          </MainContainer>

          {!props.isCreate && (
            <SideContainer>
              <Form>
                {!isTablet && <Section>{ParticipantSection}</Section>}
                <Section>
                  <FieldTitle>{t('project')}</FieldTitle>
                  <SelectWrap
                    name="project"
                    value={state.projectId}
                    options={state.projectOptions}
                    onChange={actions.onChange}
                    errors={state.errors['project']}
                    variant="filled"
                    isFullWidth
                    isDisabled={state.isEventTypeEvent}
                  />
                </Section>
              </Form>
              {props.routineEvent && state.hasWorkflows && (
                <Form>
                  <Section>
                    <FieldTitle>
                      {t('workflow')}
                      {/* <HelpLink href={helpPath('5088765')} target="_blank">
                      <HelpIcon name="help" color="inherit" />
                    </HelpLink> */}
                    </FieldTitle>
                    <ThemeProvider theme={forceLayoutTheme}>
                      <WorkflowList
                        workflows={state.workflows}
                        onChange={actions.onChangeWorkflow}
                      />
                    </ThemeProvider>
                  </Section>
                  <Section>
                    <FieldTitle>{t('linkType')}</FieldTitle>
                    <LinkContainer>
                      <SelectWrap
                        name="linkType"
                        value={state.linkType}
                        options={state.linkTypeOptions}
                        onChange={actions.onChange}
                        variant="filled"
                        isFullWidth
                        isContainerFullWidth
                      />
                      {state.linkType === LinkType.Custom && (
                        <LinkTextField
                          isNotRequired
                          name="customLink"
                          value={state.customLink}
                          placeholder={t('customLink placeholder')}
                          onChange={actions.onChange}
                          errors={state.errors['customLink']}
                          variant="filled"
                          isFullWidth
                        />
                      )}
                    </LinkContainer>
                  </Section>
                </Form>
              )}
            </SideContainer>
          )}
        </Content>
        <Footer>
          {props.onCancel && !props.isPopover && (
            <CancelButton onClick={props.onCancel} size={'large'}>
              {t('Cancel')}
            </CancelButton>
          )}
          <SubmitButton
            size={'large'}
            type="button"
            color="primary"
            isDisabled={!state.canSubmit}
            isLoading={state.isLoading}
            onClick={actions.onSubmitForm}
          >
            {t('Submit')}
          </SubmitButton>
        </Footer>
      </form>
      {state.isShowResendConfirm && (
        <SendUpdatesDialog
          isOpen={state.isShowResendConfirm}
          onCancel={actions.onCloseResendConfirmDialog}
          onSubmit={actions.onSubmit}
          isLoading={state.isLoading}
        />
      )}
    </Container>
  );
}

export const LoadingComponent = () => {
  const t = translateInComponent(translations);

  const isTablet = isMediaMax('tablet');

  return (
    <Container>
      <Content>
        <MainContainer>
          <Form isCreate={false}>
            <MetaTagList>
              <MetaTagItem>
                <Skeleton width="50px" />
              </MetaTagItem>
              <MetaTagItem>
                <Skeleton width="50px" />
              </MetaTagItem>
              <MetaTagItem>
                <Skeleton width="50px" />
              </MetaTagItem>
            </MetaTagList>
            <NameFormField>
              <Skeleton width="100%" />
            </NameFormField>

            <Section>
              {isTablet ? (
                <>
                  <FormField>
                    <InlineFieldTitle>
                      <Skeleton width="100px" />
                    </InlineFieldTitle>
                    <AlldayContainer>
                      <Skeleton width="50px" />
                    </AlldayContainer>
                  </FormField>
                  <FormField>
                    <InlineFieldTitle>
                      <Skeleton width="100px" />
                    </InlineFieldTitle>
                    <Skeleton width="100px" />
                  </FormField>
                  <FormField>
                    <InlineFieldTitle>
                      <Skeleton width="100px" />
                    </InlineFieldTitle>
                    <Skeleton width="100px" />
                  </FormField>
                  <FormField>
                    <InlineFieldTitle>
                      <Skeleton width="100px" />
                    </InlineFieldTitle>
                    <Skeleton width="100px" />
                  </FormField>
                </>
              ) : (
                <FormField>
                  <Skeleton width="100%" height="50px" />
                </FormField>
              )}
              <TopAlignFormField>
                <InlineFieldTitle>
                  <Skeleton width="100px" />
                </InlineFieldTitle>
                <DetailContainer>
                  <Skeleton width="150px" />
                </DetailContainer>
              </TopAlignFormField>
            </Section>
            <Section>
              <FormField>
                <FixedWidthInlineFieldTitle>
                  <Skeleton width="100px" />
                </FixedWidthInlineFieldTitle>
                <Skeleton width="100px" />
              </FormField>
              <FormField>
                <FixedWidthInlineFieldTitle>
                  <Skeleton width="100px" />
                </FixedWidthInlineFieldTitle>
                <Skeleton width="100px" />
              </FormField>
              <FormField>
                <FixedWidthInlineFieldTitle>
                  <Skeleton width="100px" />
                </FixedWidthInlineFieldTitle>
                <Skeleton width="100px" />
              </FormField>
            </Section>
          </Form>
        </MainContainer>
      </Content>
      <Footer>
        <CancelButton size={'large'} isDisabled>
          {t('Cancel')}
        </CancelButton>
        <SubmitButton size={'large'} type="submit" color="primary" isDisabled>
          {t('Submit')}
        </SubmitButton>
      </Footer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    background-color: ${white};
  }
`;
const Content = styled.div`
  display: flex;
  width: 960px;
  min-height: 100%;
  margin: ${spacing(40)} auto;

  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    flex-direction: column;
    width: 100%;
    margin: 0;
  }
`;
const MainContainer = styled.div`
  flex: 1;
  margin-right: ${spacing(24)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    margin-right: 0;
    padding: ${spacing(16)} ${spacing(16)} 0 ${spacing(16)};
    background-color: ${white};
  }
`;
const SideContainer = styled.div`
  width: 320px;

  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    width: initial;
    flex: 1;
    padding: ${spacing(16)};
    background-color: ${white};
  }
`;

const Form = styled.div<{ isCreate?: boolean }>`
  padding: ${spacing(24)};
  margin-bottom: ${spacing(12)};
  border: 1px solid ${gray04};
  background-color: ${white};
  border-radius: ${borderRadius(16)};

  &:last-of-type {
    margin-bottom: 0;
  }

  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    padding: 0;
    padding-bottom: ${spacing(12)};
    border: none;
    border-bottom: 1px solid ${gray04};
    border-radius: 0;

    ${({ isCreate }) =>
      isCreate &&
      css`
        border-bottom: none;
      `}
  }
`;
const Section = styled.div`
  padding-bottom: ${spacing(16)};
  margin-bottom: ${spacing(16)};
  border-bottom: 1px solid ${gray04};

  &:last-of-type {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }

  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    padding-bottom: ${spacing(12)};
    margin-bottom: ${spacing(12)};
  }
`;

const FormField = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${spacing(12)};

  &:last-of-type {
    margin-bottom: 0;
  }

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    justify-content: space-between;
    margin-bottom: ${spacing(8)};
  }
`;
const NameFormField = styled(FormField)`
  margin-bottom: ${spacing(16)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    margin-bottom: ${spacing(12)};
  }
`;
const TopAlignFormField = styled(FormField)`
  align-items: flex-start;
`;

const FieldTitle = styled.div`
  display: flex;
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(12)};
`;
const InlineFieldTitle = styled(FieldTitle)`
  height: 32px;
  line-height: 32px;
  margin-right: ${spacing(8)};
  margin-bottom: 0;
`;
const FixedWidthInlineFieldTitle = styled(InlineFieldTitle)`
  width: 140px;
  flex-shrink: 0;
`;

const DetailContainer = styled.div`
  display: flex;
  flex-direction: row;

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const HelpLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-left: ${spacing(4)};
`;
const HelpIcon = styled(Icon)`
  width: 16px !important;
  height: 16px !important;
  fill: ${gray02};
`;

const MetaTagList = styled.div`
  display: flex;
  position: relative;
  margin-bottom: ${spacing(16)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    overflow-x: auto;
    margin-bottom: ${spacing(8)};
    padding-bottom: ${spacing(8)};
  }
`;
const MetaTagItem = styled.div`
  margin-right: ${spacing(8)};
  flex-shrink: 0;

  &:last-of-type {
    margin-right: 0;
  }
`;

const TextFieldWrap = styled(TextField)`
  .MuiInput-underline:before {
    border-bottom: 1px solid ${gray04};
  }

  .MuiFilledInput-root {
    padding: ${spacing(8)};
    background-color: ${gray06};
    font-size: ${fontSize(14)};
  }
  .MuiFilledInput-root:hover {
    background-color: ${gray05};
  }
  .MuiFilledInput-input {
    padding: 0;
  }
  .MuiFilledInput-underline:before {
    border: none;
  }
  .MuiFilledInput-underline:after {
    border: none;
  }
`;
const LinkTextField = styled(TextFieldWrap)`
  margin-top: ${spacing(8)};
`;

const SelectWrap = styled(Select)`
  .MuiFilledInput-root {
    background-color: ${gray06};
    font-size: ${fontSize(14)};
  }
  .MuiFilledInput-root:hover {
    background-color: ${gray05};
  }
  .MuiFilledInput-input {
    padding: ${spacing(8)} ${spacing(32)} ${spacing(8)} ${spacing(12)};
  }
  .MuiFilledInput-underline:before {
    border: none;
  }
  .MuiFilledInput-underline:after {
    border: none;
  }
`;
const TimeZoneSelect = styled(SelectWrap)`
  width: 128px;
  margin-right: ${spacing(4)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 178px;
    margin-right: 0;
  }
`;
const EndDateSelect = styled(SelectWrap)`
  margin-right: ${spacing(4)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 180px;
    margin-right: 0;
    margin-bottom: ${spacing(4)};
  }
`;
const FixedWidthSelect = styled(SelectWrap)`
  width: 180px;
  ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
    width: 138px;
  }
`;
const RepeatSelect = styled(SelectWrap)`
  width: 70px;
  min-width: initial;
  margin-right: ${spacing(4)};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 100%;
    margin-right: 0;
    margin-bottom: ${spacing(4)};
  }
`;
const LocationSelect = styled(SelectWrap)`
  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 180px;
  }
  ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
    width: 138px;
  }
`;
const EventUserSelect = styled(SelectWrap)`
  width: 160px;
`;

const DateTimeHyphen = styled.div`
  font-size: ${fontSize(10)};
  margin-right: ${spacing(4)};
`;
const AlldayContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};

  .MuiCheckbox-root {
    padding: ${spacing(3)};
  }

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 178px;
  }
`;
const RoutineScheduleFormContainer = styled.div`
  padding-bottom: ${spacing(24)};
  margin-bottom: ${spacing(24)};
  border-bottom: 1px solid ${gray04};

  :last-of-type {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 100%;
    padding-bottom: ${spacing(12)};
    margin-bottom: ${spacing(12)};
  }
`;

const DescriptionContainer = styled.div`
  margin-top: ${spacing(12)};

  & .MuiOutlinedInput-multiline {
    padding: ${spacing(6)} ${spacing(8)};
    font-size: ${fontSize(13)};
  }
`;
const LinkContainer = styled.div`
  margin-top: ${spacing(8)};
`;

export const Description = styled.div`
  font-size: ${fontSize(12)};
  margin-bottom: ${spacing(16)};
`;

const EventTypeUserItem = styled.div<{
  isFirstItem?: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  /* padding-bottom: ${spacing(12)}; */
  /* margin-bottom: ${spacing(12)}; */
  /* border-bottom: 1px solid ${gray05}; */


  ${({ isFirstItem }) =>
    isFirstItem &&
    css`
      margin-top: ${spacing(20)};
    `}
`;
const EventTypeUserMainItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: ${spacing(4)};
  flex-grow: 1;
`;
const EventUserAvatar = styled(Avatar)`
  width: 24px;
  height: 24px;
`;
const EventTypeUserContents = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 48px;
`;
const EventTypeUserActionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 130px;
  margin: ${spacing(12)} 0 ${spacing(12)} 0;
`;
const UserName = styled.span`
  flex-grow: 1;
  margin-left: ${spacing(8)};
  font-size: ${fontSize(13)};
  word-break: break-word;
`;
const IconButtonWrap = styled(IconButton)`
  padding: ${spacing(3)};
  margin-left: ${spacing(8)};
`;
const DeleteIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
`;
const WarnIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
  color: ${guest};
`;

const LocationContainer = styled.div``;
const LocationTitle = styled.div`
  margin: ${spacing(8)} 0;
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
`;
const LocationImage = styled(Image)`
  width: 19px;
  margin-right: ${spacing(8)};
`;
const LocationTextFieldContainer = styled.div`
  margin-top: ${spacing(8)};
  ${(props) => mediaMaxWithLayoutTheme(props, 'smartphone')} {
    width: 138px;
  }
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  height: 86px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${white};
  border-top: 1px solid ${gray05};

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    bottom: -8px;
    height: 64px;
  }
`;
const SubmitButton = styled(Button)`
  width: 240px;
  margin-left: ${spacing(24)};
  :first-of-type {
    margin-left: 0;
  }
  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 120px;
    min-height: initial;
    height: 40px;
    margin-left: ${spacing(12)};
  }
`;
const CancelButton = styled(Button)`
  width: 240px;
  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    width: 120px;
    min-height: initial;
    height: 40px;
  }
`;

const CloseButtonWrap = styled(IconButton)`
  padding: ${spacing(4)};
  position: absolute;
  top: 0;
  right: 0;
`;
const CloseIcon = styled(Icon)`
  color: ${gray01};
`;

const EventTypeUserListItemContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const CreateTypeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(8)};
`;
const CreateType = styled.div<{ isActive: boolean }>`
  cursor: pointer;
  border-radius: ${borderRadius(6)};
  background-color: ${(props) => (props.isActive ? blue03 : 'transparent')};
  color: ${(props) => (props.isActive ? primary : gray01)};
  padding: ${spacing(4)} ${spacing(8)};
  margin-right: ${spacing(8)};
  font-size: ${fontSize(12)};
  :last-of-type {
    margin-right: 0;
  }
`;

const DescriptionTextArea = styled(TextArea)`
  .MuiFilledInput-multiline {
    padding: ${spacing(8)} ${spacing(8)};
    font-size: ${fontSize(14)};
  }
  .MuiInputBase-root {
    background-color: ${gray06};
  }
  .MuiFilledInput-underline:before {
    border: 0;
  }
  .MuiFilledInput-underline:after {
    border: 0;
  }
  .MuiFilledInput-root:hover {
    background-color: ${gray05};
  }
`;
